/**
 * client/src/pages/Landing.tsx — a calm, explanatory front door.
 *
 * The primary journey remains anonymous and immediate. The three short explanations answer the
 * questions the recording flow repeatedly surfaced in testing — what will happen, when to begin,
 * and whether finished audio is kept — without adding another decision before the main action.
 */

import { FAMILY } from "../../../shared/voice/copy.ts";
import { useSession } from "../lib/session.tsx";
import { LinkButton, Waveform, Wordmark } from "../ui/primitives.tsx";

const DOOR_WAVE = [
  0.16, 0.28, 0.2, 0.38, 0.56, 0.34, 0.72, 0.46, 0.84, 0.4, 0.62, 0.3, 0.5, 0.78, 0.36, 0.88,
  0.54, 0.7, 0.24, 0.44, 0.64, 0.32, 0.76, 0.5, 0.86, 0.42, 0.68, 0.26, 0.48, 0.74, 0.34, 0.58,
] as const;

export function Landing() {
  const { loading, signedIn, isGod } = useSession();

  return (
    <main className="vox-landing">
      <header className="vox-landing-header">
        <Wordmark />
        {!loading && (
          <LinkButton to={signedIn ? (isGod ? "/admin" : "/voices") : "/signin"} variant="quiet">
            {signedIn ? FAMILY.door.signInLead : FAMILY.door.signInTitle}
          </LinkButton>
        )}
      </header>

      <section className="vox-landing-hero">
        <div className="vox-landing-copy">
          <h1 className="vox-landing-title">{FAMILY.door.hero}</h1>
          <p className="vox-lead">{FAMILY.door.what}</p>
          <div className="vox-landing-primary">
            <LinkButton to="/make" variant="primary" hand>
              {FAMILY.door.primary}
            </LinkButton>
          </div>
          <p className="vox-landing-meta">{FAMILY.door.noAccount}</p>
          <p className="vox-landing-safe">
            <strong>{FAMILY.door.consentFirst}</strong> {FAMILY.door.safe}
          </p>
        </div>

        <section className="vox-landing-demo" aria-label={FAMILY.door.demoWaveLabel}>
          <Waveform peaks={DOOR_WAVE} label={FAMILY.door.demoWaveLabel} />
          <ol className="vox-landing-steps">
            <DoorStep number="1" title={FAMILY.door.stepExampleTitle} body={FAMILY.door.stepExampleBody} />
            <DoorStep number="2" title={FAMILY.door.stepBeepsTitle} body={FAMILY.door.stepBeepsBody} />
            <DoorStep number="3" title={FAMILY.door.stepSpeakTitle} body={FAMILY.door.stepSpeakBody} />
          </ol>
        </section>
      </section>

      <footer className="vox-landing-footer">{FAMILY.door.outcome}</footer>
    </main>
  );
}

function DoorStep({ number, title, body }: { number: string; title: string; body: string }) {
  return (
    <li className="vox-landing-step">
      <span className="vox-landing-step-num" aria-hidden="true">{number}</span>
      <span>
        <strong>{title}</strong>
        <span>{body}</span>
      </span>
    </li>
  );
}
